import {
  type EdgeData,
  type EdgeDirection,
  type EdgeId,
  type EdgeRecord,
  type NodeData,
  type NodeId,
  safeAbsurd,
} from '@ipfabric/utils';
import { plainSet } from '@ipfabric/utils/fp/plainSet';
import { prop } from '@ipfabric/utils/fp/prop';
import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { useEffect, useState } from 'react';

import {
  nbsp,
  rightArrow,
  rightLeftArrow,
} from '@ipfabric/frontend-shared-assets/utils/characters';
import { labelsToStringArray } from 'components/Graph/GraphApp/GraphEdge';
import { escapeRegExp } from 'utils/escapeRegExp';
import { SEARCH_HIGHLIGHT_COLOR } from '../GraphApp/colors';
import { useGraphContextSelectors } from '../GraphContext';
import type { SearchResult } from '../GraphState';

import { highlightEdge } from 'components/Graph/commands/edges/highlightEdge';
import { unhighlightEdge } from 'components/Graph/commands/edges/unhighlightEdge';
import { fitNodeToView } from 'components/Graph/commands/nodes/fitNodeToView';
import { setSearchResult } from 'components/Graph/commands/search/setSearchResult';
import { EDGE_HIGHLIGHT_THICKNESS } from '../constants';
import { selectNotHiddenNodes } from '../model/selectors/selectNotHiddenNodes';

interface GraphSearchHook {
  previewEdge: (edgeData: EdgeData) => void;
  previewNode: (nodeData: NodeData) => void;
  resetPreview: () => void;
  // fetches results for autocomplete
  matchQuery: (query: string) => void;
  searchMatches: GrahpSearchMatches;
  search: (query: string) => void;
}

interface NodeSearchMatch {
  label: string;
  nodeData: NodeData;
}

interface EdgeSearchMatch {
  label: string;
  sublabel: string;
  edgeData: EdgeData;
}

export interface GrahpSearchMatches {
  edges: EdgeSearchMatch[];
  nodes: NodeSearchMatch[];
  nodeExactMatch: NodeData | null;
}

const initialMatches = {
  edges: [],
  nodes: [],
  nodeExactMatch: null,
};

export const getEdgeArrow = (edgeDirection: EdgeDirection): string => {
  switch (edgeDirection) {
    case 'directed':
      return rightArrow;
    case 'bidirected':
      return rightLeftArrow;
    case 'undirected':
      return '-';
    default:
      return safeAbsurd(edgeDirection, 'Missing edge type', '');
  }
};

const getEdgeSublabel = (
  labels: string[],
  direction: EdgeDirection,
  sourceNodeLabel: string | null,
  targetNodeLabel: string | null,
) => {
  if ((!labels[0] || !labels[2]) && (!sourceNodeLabel || !targetNodeLabel)) {
    return '';
  }

  const source = `${sourceNodeLabel ?? ''} ${labels[0] ? `(${labels[0]})` : ''}`;
  const target = `${targetNodeLabel ?? ''} ${labels[2] ? `(${labels[2]})` : ''}`;

  return `${source} ${nbsp}${getEdgeArrow(direction)}${nbsp} ${target}`;
};

const makeFindAdjacenNodeIds =
  (edges: EdgeRecord, initialNodeIds: NodeId[] = []) =>
  (edgeIds: EdgeId[]) =>
    edgeIds.reduce((acc, edgeId) => {
      // Fixed noUncheckedIndexedAccess NIM-19408
      acc.add(edges[edgeId]!.source);
      acc.add(edges[edgeId]!.target);

      return acc;
    }, new Set<NodeId>(initialNodeIds));

export const useGraphSearch = (): GraphSearchHook => {
  const [searchMatches, setSearchMatches] =
    useState<GrahpSearchMatches>(initialMatches);
  const [previewedEdgeId, setPreviewedEdgeId] = useState<EdgeId | null>(null);
  const [searchResult, setLocalSearchResult] = useState<SearchResult | null>(
    null,
  );
  const [activeQuery, setActiveQuery] = useState<string>('');

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    edges: prop('graph', 'edges'),
    nodes: selectNotHiddenNodes,
    nodesEdgesMap: prop('graph', 'nodesEdgesMap'),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger only on nodes change
  useEffect(() => {
    if (activeQuery) {
      search(activeQuery, false);
    }
  }, [ctxProps.nodes]);

  const previewEdge = (edge: EdgeData) => {
    resetPreview();
    ctxProps.dispatch(setSearchResult, {
      searchResult: {
        nodeExactMatch: null,
        edgeIds: new Set<EdgeId>([edge.id]),
        nodeIds: new Set<NodeId>(),
      },
    });

    setPreviewedEdgeId(edge.id);
    ctxProps.dispatch(highlightEdge, {
      color: SEARCH_HIGHLIGHT_COLOR,
      edgeId: edge.id,
      thickness: EDGE_HIGHLIGHT_THICKNESS,
    });
  };

  const previewNode = (nodeData: NodeData) => {
    ctxProps.dispatch(setSearchResult, {
      searchResult: {
        nodeExactMatch: nodeData,
        edgeIds: new Set<EdgeId>(),
        nodeIds: new Set<NodeId>([nodeData.id]),
      },
    });
  };

  const resetPreview = () => {
    ctxProps.dispatch(setSearchResult, { searchResult });

    if (previewedEdgeId) {
      ctxProps.dispatch(unhighlightEdge, {
        color: true,
        thickness: true,
        edgeId: previewedEdgeId,
      });
    }
  };

  const search = (query: string, fitExactMatch = true) => {
    resetPreview();

    if (query === '') {
      setLocalSearchResult(null);
      setSearchMatches(initialMatches);
      ctxProps.dispatch(setSearchResult, { searchResult: null });
      setActiveQuery('');

      return;
    }

    const { nodes, edges, nodeExactMatch } = matchQuery(query);

    const matchNodes = () => {
      const matchedNodeIds = nodes.map(({ nodeData }) => nodeData.id);
      const edgeIds = matchedNodeIds.reduce((acc, nodeId) => {
        // biome-ignore lint/complexity/noForEach: iteration over Set
        ctxProps.nodesEdgesMap[nodeId]?.forEach((edgeId) => {
          acc.add(edgeId);
        });

        return acc;
      }, new Set<EdgeId>());

      const nodeIds = pipe(
        edgeIds,
        plainSet.toArray,
        makeFindAdjacenNodeIds(ctxProps.edges, matchedNodeIds),
      );

      return {
        edgeIds,
        nodeIds,
      };
    };

    const matchEdges = () => {
      const edgeIds = edges.map(({ edgeData }) => edgeData.id);
      const findAdjacenNodeIds = makeFindAdjacenNodeIds(ctxProps.edges);

      const nodeIds = findAdjacenNodeIds(edgeIds);

      return {
        edgeIds: new Set<EdgeId>(edgeIds),
        nodeIds,
      };
    };

    const nodeMatch = matchNodes();
    const edgeMatch = matchEdges();

    const newSearchResult = {
      edgeIds: new Set<EdgeId>([
        ...Array.from(nodeMatch.edgeIds),
        ...Array.from(edgeMatch.edgeIds),
      ]),
      nodeExactMatch,
      nodeIds: new Set<NodeId>([
        ...Array.from(nodeMatch.nodeIds),
        ...Array.from(edgeMatch.nodeIds),
      ]),
    };

    setLocalSearchResult(newSearchResult);
    ctxProps.dispatch(setSearchResult, { searchResult: newSearchResult });

    if (nodeExactMatch && fitExactMatch) {
      ctxProps.dispatch(fitNodeToView, { nodeData: nodeExactMatch });
    }
  };

  const matchQuery = (query: string) => {
    setActiveQuery(query);
    if (query === '') {
      setSearchMatches(initialMatches);
      // clear search result on empty input
      setLocalSearchResult(null);
      ctxProps.dispatch(setSearchResult, { searchResult: null });

      return initialMatches;
    }

    const regExp = new RegExp(escapeRegExp(query), 'i');
    const regExpExact = new RegExp(`^${escapeRegExp(query)}$`, 'i');

    const matchNodes = () => {
      const matchedNodes = pipe(
        ctxProps.nodes,
        record.filter((nodeData) => regExp.test(nodeData.label)),
      );

      const matches = record.reduce<NodeData, NodeSearchMatch[]>(
        [],
        (acc, nodeData) => {
          acc.push({
            label: nodeData.label,
            nodeData,
          });

          return acc;
        },
      )(matchedNodes);

      return matches;
    };

    const matchEdges = () => {
      const matches = record.reduce<EdgeData, EdgeSearchMatch[]>(
        [],
        (acc, edgeData) => {
          const labels = labelsToStringArray(edgeData.labels);
          const labelMatch = labels.find((label) => regExp.test(label));
          const sourceNodeLabel =
            ctxProps.nodes[edgeData.source]?.label ?? null;
          const targetNodeLabel =
            ctxProps.nodes[edgeData.target]?.label ?? null;

          if (labelMatch) {
            acc.push({
              label: labelMatch,
              sublabel: getEdgeSublabel(
                labels,
                edgeData.direction,
                sourceNodeLabel,
                targetNodeLabel,
              ),
              edgeData,
            });
          }

          return acc;
        },
      )(ctxProps.edges);

      return matches;
    };

    const nodes = matchNodes();
    const nodeExactMatch = pipe(
      nodes,
      array.map(prop('nodeData')),
      array.filter((nodeData) => regExpExact.test(nodeData.label)),
      Object.values,
      (values) => (values.length > 0 ? values[0] : null),
    );

    const newSearchMatches = {
      edges: matchEdges(),
      nodes,
      nodeExactMatch,
    };
    setSearchMatches(newSearchMatches);

    if (nodeExactMatch) {
      ctxProps.dispatch(setSearchResult, {
        searchResult: {
          nodeIds: new Set<NodeId>(),
          edgeIds: new Set<EdgeId>(),
          nodeExactMatch,
        },
      });
    } else {
      resetPreview();
    }

    return newSearchMatches;
  };

  return {
    previewEdge,
    previewNode,
    resetPreview,
    matchQuery,
    searchMatches,
    search,
  };
};
